import React, { useEffect, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { selectFleetData } from 'store/fleet-management/fleet-management-slice-selectors';
import FleetDataSet from 'models/fleet-management/FleetDataSet';
import TableWidget from '../widget/TableWidget';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getFleetData } from 'store/fleet-management/fleet-management-actions';
import { replaceArgsInMessage } from '@common/utils/strings';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import { OpenFullScreenIcon } from '@common/utils/icons/icons';
import DownloadCsvDataButton from '../DownloadCsvDataButton';

const DataPreview: React.FC<{ dataset?: FleetDataSet }> = ({ dataset }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const liveData = useAppSelector((state) => selectFleetData(state)).find(
    (l) => l.id === dataset?.id,
  );

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (
      dataset?.id != null &&
      (liveData == null || liveData.rest.getStatus === HttpFetchStatus.Idle)
    ) {
      dispatch(getFleetData(dataset.id));
    }
  }, [dataset?.id, dispatch, liveData]);

  const openFullscreenDataViewClickHandler = () => {
    setIsFullscreen(true);
  };

  const closeFullscreenDataViewHandler = () => {
    setIsFullscreen(false);
  };

  if (dataset == null) return null;

  return (
    <React.Fragment>
      <Stack
        flex={1}
        sx={(theme) => ({
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          overflow: 'hidden',
        })}
        p={2}
        spacing={2}
      >
        <Stack direction="row" spacing={1}>
          <Typography variant="h2" flex={1} sx={(theme) => ({ lineHeight: theme.spacing(5) })}>
            {t('title-dataset-preview-data')}
          </Typography>
          <IconButton onClick={openFullscreenDataViewClickHandler}>
            <OpenFullScreenIcon />
          </IconButton>
          <DownloadCsvDataButton id={dataset.id} name={dataset.name} />
        </Stack>
        {liveData == null ? (
          <CustomAlert severity="info" header={t('info-could-not-find-live-data')} />
        ) : (
          <React.Fragment>
            <TableWidget
              fields={dataset.fields}
              data={liveData.data != null ? liveData.data.slice(0, 15) : null}
              getStatus={liveData?.rest.getStatus}
              hideFooter
            />
            {liveData.loadTime != null && (
              <Typography variant="subtitle2">
                {replaceArgsInMessage(t('info-data-load-time'), [
                  (liveData.loadTime / 1000).toFixed(1),
                  t('label-seconds-very-short'),
                ])}
              </Typography>
            )}
          </React.Fragment>
        )}
      </Stack>
      <DialogWrapperWithClose
        onClose={closeFullscreenDataViewHandler}
        open={isFullscreen}
        fullScreen
        title={dataset.name}
      >
        <DialogContent>
          {liveData != null && (
            <TableWidget
              fields={dataset.fields}
              data={liveData.data}
              getStatus={liveData.rest.getStatus}
            />
          )}
        </DialogContent>
      </DialogWrapperWithClose>
    </React.Fragment>
  );
};

export default DataPreview;
